import { useMemo, useState } from "react";
import { useParams } from "react-router";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import styled from "styled-components";

/* Global Types & Enums */
import { TeamStatus } from "@coworker/apprestructured/src/shared/types/team";

/* Global Hooks */
import { useActiveStoreUsers } from "@coworker/apprestructured/src/shared/hooks/users/useActiveStoreUsers";
import { useTranslation } from "@coworker/locales";

/* Teams Management Hooks */
import useTeamManagerFilters from "../hooks/filters/useTeamManagerFilters";
import useFilteredUsers from "../hooks/useFilteredUsers";

/* Global Components */
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { FilterPanel } from "@coworker/apprestructured/src/shared/filters/components/FilterPanel";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";

/* Teams Management Components */
import MyIdentityAccessRequestComponent from "@coworker/apprestructured/src/login/components/Unauthorized/MyIdentityAccessRequestComponent";
import useHasTeamEditorPermissions from "../hooks/useTeamEditorPermissions";
import useTeamsWithUsersCount from "../hooks/useTeamsWithUsersCount";
import AddUserModal from "./AddUserModal";
import TeamUsersTable from "./Tables/TeamUsersTable";
import EditTeamModal from "./TeamModal/EditTeamModal/EditTeamModal";

const Container = styled.div`
  padding: 32px 64px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  @media (max-width: 768px) {
    padding: 16px 8px;
  }
`;

const ActionsSection = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const SearchAndButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const TeamManager = () => {
  const { t } = useTranslation();
  const { storeId: rawStoreId, teamId } = useParams();
  const storeId = useMemo(() => {
    if (!rawStoreId) return "";

    try {
      return decodeURIComponent(rawStoreId);
    } catch {
      return rawStoreId;
    }
  }, [rawStoreId]);
  const teamAdminLink = useMemo(
    () => `/teams/store/${encodeURIComponent(storeId)}`,
    [storeId]
  );
  const navigate = useFixaNavigate();

  const [usersSearchQuery, setUsersSearchQuery] = useState("");
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [editTeamModalOpen, setEditTeamModalOpen] = useState(false);

  const {
    data: teams,
    isLoading: isLoadingTeams,
    isFetching: isFetchingTeams,
  } = useTeamsWithUsersCount(storeId);
  const team = useMemo(() => {
    return teams?.find((team) => team.id === teamId) || undefined;
  }, [teams, teamId, isLoadingTeams, isFetchingTeams]);

  const hasActiveProductQualityTeam = useMemo(() => {
    if (!teams) return false;
    const currentTeamIsPQ = team?.is_product_quality ?? false;
    return teams.some(
      (t) =>
        t.is_product_quality &&
        t.status === TeamStatus.ACTIVE &&
        !(currentTeamIsPQ && t.id === teamId)
    );
  }, [teams, teamId, team]);

  const {
    data: allUsers,
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
  } = useActiveStoreUsers(storeId);
  const teamUsers = useMemo(() => {
    if (!allUsers || !team) return [];
    return allUsers.filter((user) => user.teamId === team.id);
  }, [allUsers, team]);

  const { filterValues, setFilterValues, useFiltersFactory, resetFilters } =
    useTeamManagerFilters(teamUsers || [], teamId || "");

  const filteredUsers = useFilteredUsers(
    teamUsers || [],
    [],
    filterValues,
    isFetchingUsers || isLoadingUsers,
    teamId,
    usersSearchQuery
  );

  const { hasPermission: isUserAuthorized, isLoading: isLoadingAuthorization } =
    useHasTeamEditorPermissions(storeId);

  if (!isUserAuthorized && !isLoadingAuthorization)
    return (
      <div>
        <Linkbar
          items={[{ label: t("teamManagementString"), to: teamAdminLink }]}
          currentLabel=""
        />
        <MyIdentityAccessRequestComponent
          titleKey="unauthorizedTeamsManagementTitleString"
          descriptionKey="unauthorizedTeamsManagementDescriptionString"
        />
      </div>
    );

  return (
    <div>
      <Linkbar
        loading={isFetchingTeams || isLoadingTeams}
        items={[
          {
            label: t("teamManagementString"),
            to: teamAdminLink,
          },
        ]}
        currentLabel={team?.name || ""}
        currentAsH1
      />
      <Container>
        <ActionsSection>
          <div>
            <FilterPanel
              filterValues={filterValues}
              setFilterValues={setFilterValues}
              useFiltersFactory={useFiltersFactory}
              feature="teamManager"
            />
          </div>
          <div>
            <SearchAndButtonsWrapper>
              <FixaSearch
                id="search"
                onClear={() => setUsersSearchQuery("")}
                placeholder={t("searchUsersString")}
                value={usersSearchQuery}
                onChange={(e) => setUsersSearchQuery(e.currentTarget.value)}
              />
              <FixaButton
                onClick={() => setAddUserModalOpen(true)}
                text={t("addUserString")}
                size="medium"
                type="primary"
              />
              <FixaButton
                onClick={() => setEditTeamModalOpen(true)}
                text={t("editTeamString")}
                size="medium"
                type="secondary"
              />
            </SearchAndButtonsWrapper>
          </div>
        </ActionsSection>
        <TeamUsersTable
          users={filteredUsers || []}
          selectedTeam={team}
          isLoading={isFetchingUsers || isLoadingUsers}
          resetFilters={resetFilters}
        />
      </Container>
      {!!storeId && team && (
        <>
          <AddUserModal
            visible={addUserModalOpen}
            team={team}
            close={() => setAddUserModalOpen(false)}
            users={allUsers}
            storeId={storeId}
          />
          <EditTeamModal
            visible={editTeamModalOpen}
            close={() => setEditTeamModalOpen(false)}
            hasActiveProductQualityTeam={hasActiveProductQualityTeam}
            selectedTeam={team}
            resetFilters={resetFilters}
            selectedTeamUsers={teamUsers}
            storeTeams={teams}
            onDelete={() => navigate(teamAdminLink)}
          />
        </>
      )}
    </div>
  );
};

export default TeamManager;
